import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useScroll, useMotionValueEvent, useInView, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowRight, TrendingUp, ShieldCheck, Clock, ChevronDown, KeyRound, LineChart, PenLine, Banknote } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import StadiumScene from "@/components/stadium/StadiumScene";

const GATES = [
  {
    id: "product-card-artist-loans",
    gate: "Gate 01 · The Entrance",
    title: "Artist Capital Loans",
    slug: "artist-capital-loans",
    range: "$25K – $1M",
    copy: "Your walk-out music starts here. Working capital and term loans for recording, touring, and marketing — let us get you funded.",
    side: "left",
  },
  {
    id: "product-card-label-credit",
    gate: "Gate 02 · The Stands",
    title: "Label Lines of Credit",
    slug: "label-lines-of-credit",
    range: "$100K – $5M",
    copy: "Fill every seat. Flexible credit you draw on for new signings, releases, and operations — and minimize your cost of capital.",
    side: "right",
  },
  {
    id: "product-card-credit-cards",
    gate: "Gate 03 · Midfield",
    title: "Business Credit Cards",
    slug: "business-credit-cards",
    range: "$10K – $250K",
    copy: "Play the field. A curated network of business credit card lenders, hand-selected for the best fit — without wasting inquiries.",
    side: "left",
  },
  {
    id: "product-card-catalog-advances",
    gate: "Gate 04 · The Main Stage",
    title: "Catalog Advances",
    slug: "catalog-advances",
    range: "$50K – $10M",
    copy: "Headline your own future. Unlock the value of your masters and publishing — and retain 100% of your rights and ownership.",
    side: "right",
  },
];

const RAIL_LABELS = ["Entrance", "Stands", "Midfield", "Stage"];

const TRINITY = [
  {
    num: "I",
    title: "Streaming & Revenue Performance",
    copy: "We position your royalty statements and streaming history as what they truly are — bankable, recurring income.",
  },
  {
    num: "II",
    title: "Credit & Cash Flow Integrity",
    copy: "We structure your profile so lenders see strength, not risk — clarifying your goals before a single inquiry is pulled.",
  },
  {
    num: "III",
    title: "Growth & Catalog Strategy",
    copy: "We match your vision to the right capital, at the right terms — positioning you as irresistibly lendable.",
  },
];

const STEPS = [
  {
    num: "01",
    icon: KeyRound,
    tag: "5-Minute Application",
    title: "Unlock the Vault",
    copy: "Submit your application in minutes. No obligation, no upfront fees — just clarity on what you qualify for.",
  },
  {
    num: "02",
    icon: LineChart,
    tag: "Options Within 24 Hours",
    title: "Capital Assessment",
    copy: "Our team reviews your catalog, streaming performance, and credit profile — and returns with options within 24 hours.",
  },
  {
    num: "03",
    icon: PenLine,
    tag: "Structured Around You",
    title: "Tailored Terms",
    copy: "We negotiate the limits, the payment terms, and the structure around your goals — not the lender's.",
  },
  {
    num: "04",
    icon: Banknote,
    tag: "Funded In A Week Or Less",
    title: "Capital Deployed",
    copy: "Funds move fast. Offers on new projects and signings in a week or less — so you never miss a moment.",
  },
];

const STATS = [
  { prefix: "$", value: 15, suffix: "M+", label: "Capital Unlocked" },
  { value: 83, suffix: "%", label: "Approved" },
  { value: 24, suffix: "HR", label: "Average Assessment" },
  { value: 100, suffix: "%", label: "Rights Retained" },
];

const StatNumber = ({ prefix = "", value, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    function tick(now) {
      const t = Math.min(1, Math.max(0, (now - start) / 1800));
      setDisplay(Math.round((1 - Math.pow(1 - t, 4)) * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

const TESTIMONIALS = [
  {
    quote: "Emison gave us the financial muscle to compete for signings we used to lose to the majors.",
    role: "Label Founder · Lagos",
  },
  {
    quote: "Straightforward and rewarding. They offered a range of options and terms that made the decision simple.",
    role: "Independent Artist · Atlanta",
  },
  {
    quote: "Flexible funding helped us add thousands of artists to our platform and double revenue two years running.",
    role: "Distributor CEO · Los Angeles",
  },
];

const FAQ_PREVIEW = [
  {
    q: "Do I give up ownership of my music?",
    a: "No. Every structure we arrange — loans, lines of credit, credit cards — is debt, not equity. You retain 100% of your masters, publishing, and rights.",
  },
  {
    q: "How fast can I get funded?",
    a: "Assessments come back within 24 hours, and offers on new projects typically land in a week or less.",
  },
  {
    q: "What can I qualify for?",
    a: "Funding ranges from $25,000 to $10M+ depending on your streaming performance, royalty income, and credit profile.",
  },
];

const CONFETTI_COLORS = ["#d4af37", "#f3e5ab", "#c9a227", "#ffffff", "#8c6d3f"];
const fireConfetti = () => {
  const defaults = {
    colors: CONFETTI_COLORS,
    ticks: 220,
    gravity: 0.9,
    scalar: 1.05,
    zIndex: 40,
  };
  confetti({ ...defaults, particleCount: 90, spread: 75, origin: { x: 0.2, y: 0.7 } });
  confetti({ ...defaults, particleCount: 90, spread: 75, origin: { x: 0.8, y: 0.7 } });
  setTimeout(
    () =>
      confetti({ ...defaults, particleCount: 150, spread: 110, origin: { x: 0.5, y: 0.6 } }),
    350
  );
};

const Home = () => {
  const journeyRef = useRef(null);
  const confettiFired = useRef(false);
  const progressRef = useRef(0);
  const [activeGate, setActiveGate] = useState(-1);
  const { scrollYProgress } = useScroll({
    target: journeyRef,
    offset: ["start start", "end end"],
  });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    progressRef.current = v;
    const g = Math.floor((v - 0.14) / 0.19);
    setActiveGate(v < 0.14 ? -1 : Math.min(3, Math.max(0, g)));
  });

  const [streams, setStreams] = useState(500000);
  const [royalty, setRoyalty] = useState(10000);
  const annual = royalty * 12;
  const mult = 1 + Math.min(streams / 10000000, 0.5);
  const low = Math.max(25000, Math.round((annual * 1.5 * mult) / 5000) * 5000);
  const high = Math.min(10000000, Math.round((annual * 3.5 * mult) / 5000) * 5000);
  const fmt = (n) =>
    n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`;

  return (
    <main data-testid="landing-page">
      {/* STADIUM JOURNEY */}
      <section ref={journeyRef} id="products" className="relative">
        <div className="sticky top-0 h-screen pointer-events-none">
          <StadiumScene progressRef={progressRef} />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 45%, rgba(90,62,18,0.35) 100%)",
            }}
          />
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-espresso to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-espresso/80 to-transparent" />
          {/* Progress rail */}
          <div className="absolute right-5 lg:right-10 top-1/2 -translate-y-1/2 flex flex-col gap-6 items-end">
            {RAIL_LABELS.map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <span
                  className={`hidden lg:block text-[10px] font-mono-accent uppercase tracking-[0.25em] transition-colors duration-500 ${
                    activeGate === i ? "text-gold" : "text-stone/40"
                  }`}
                >
                  {label}
                </span>
                <span
                  data-testid={`rail-gate-${i + 1}`}
                  className={`w-2 h-2 rotate-45 transition-all duration-500 ${
                    activeGate === i
                      ? "bg-gold shadow-[0_0_12px_rgba(212,175,55,0.8)] scale-125"
                      : "bg-stone/30"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 -mt-[100vh]">
          {/* HERO — outside the tunnel */}
          <div className="min-h-screen flex items-center">
            <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full pt-24">
              <div className="relative bg-[#fffdf9] border border-[#e3d3ac] max-w-2xl shadow-[0_30px_80px_rgba(45,26,11,0.25)]">
                <span
                  className="absolute bottom-0 left-0 right-0 h-[3px]"
                  style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                />
                <div className="p-10 lg:p-14">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="flex items-center justify-between mb-10"
              >
                <p className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-stone">
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
                  Emison Management — Your Funding Partner
                </p>
                <span
                  className="w-10 h-10 rotate-45 flex items-center justify-center shrink-0 ml-4 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                  style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                >
                  <span className="-rotate-45 font-serif italic text-white text-sm">E</span>
                </span>
              </motion.div>
              <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-cream max-w-3xl">
                <MaskedLines
                  lines={[
                    <>Welcome to</>,
                    <span className="italic gold-text">the big leagues.</span>,
                  ]}
                />
              </h1>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.6 }}
                className="block mt-10 h-px w-24 bg-gradient-to-r from-[#c9a227] to-transparent"
              />
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 text-base sm:text-lg text-sand leading-relaxed max-w-xl"
              >
                Take the tunnel. Scroll through the stadium and tour four ways
                we get artists and record labels funded — then take the stage.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 flex flex-wrap gap-4 pointer-events-auto"
              >
                <Link
                  to="/apply"
                  data-testid="cta-unlock-vault-hero"
                  className="sheen-btn text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform flex items-center gap-3"
                  style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
                >
                  Go Backstage <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#gate-1"
                  data-testid="cta-explore-products"
                  className="group/tour inline-flex items-center gap-3 text-[11px] font-mono-accent uppercase tracking-[0.25em] text-champagne px-2 py-4"
                >
                  <span className="border-b border-[#c9a227]/50 pb-1 group-hover/tour:border-[#c9a227] group-hover/tour:text-gold transition-colors">
                    Begin The Tour
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover/tour:translate-x-1 transition-transform" />
                </a>
              </motion.div>
                </div>
              </div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="mt-20 text-gold/70"
              >
                <ChevronDown className="w-6 h-6" />
              </motion.div>
            </div>
          </div>

          {/* GATES */}
          {GATES.map((g, i) => (
            <div
              key={g.id}
              id={`gate-${i + 1}`}
              className={`min-h-[130vh] flex items-center px-6 lg:px-12 ${
                g.side === "left" ? "justify-start" : "justify-end"
              }`}
            >
              <Reveal className="max-w-xl" y={72}>
                <div
                  data-testid={g.id}
                  className="group relative overflow-hidden bg-[#fffdf9] border border-[#e3d3ac] shadow-[0_30px_80px_rgba(45,26,11,0.25)] transition-all duration-700 hover:-translate-y-1.5 hover:shadow-[0_45px_100px_rgba(201,162,39,0.35)]"
                >
                  <motion.span
                    aria-hidden="true"
                    initial={{ x: "-160%" }}
                    whileInView={{ x: "260%" }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1.5, delay: 0.45 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                    className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12 z-10"
                    style={{ background: "linear-gradient(90deg, transparent, rgba(229,193,88,0.35), transparent)" }}
                  />
                  <span
                    className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                  />
                  <div className="p-10 lg:p-14">
                    <div className="flex items-center justify-between mb-10">
                      <span className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-stone">
                        <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
                        {g.gate}
                      </span>
                      <span
                        className="w-10 h-10 rotate-45 flex items-center justify-center shrink-0 shadow-[0_8px_20px_rgba(201,162,39,0.4)] transition-transform duration-700 group-hover:rotate-[135deg]"
                        style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                      >
                        <span className="-rotate-45 font-serif italic text-white text-sm transition-transform duration-700 group-hover:-rotate-[135deg]">E</span>
                      </span>
                    </div>
                    <h2 className="font-serif font-semibold text-4xl sm:text-[2.75rem] leading-[1.08] tracking-tight text-cream">
                      {g.title}
                    </h2>
                    <div className="mt-8 flex items-baseline gap-4">
                      <span className="font-serif font-semibold text-2xl sm:text-3xl gold-text">{g.range}</span>
                      <span className="text-[9px] font-mono-accent uppercase tracking-[0.3em] text-stone">Funding Range</span>
                    </div>
                    <span className="mt-7 block h-px w-full bg-gradient-to-r from-[#c9a227]/70 via-[#c9a227]/25 to-transparent" />
                    <p className="mt-7 text-sm sm:text-base text-sand leading-relaxed">
                      {g.copy}
                    </p>
                    <div className="mt-9 flex flex-wrap items-center gap-5">
                      <Link
                        to="/apply"
                        className="sheen-btn inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-7 py-3.5 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform"
                        style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
                      >
                        Go Backstage <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      <Link
                        to={`/products/${g.slug}`}
                        data-testid={`learn-more-${g.slug}`}
                        className="group/lm inline-flex items-center gap-3 text-[11px] font-mono-accent uppercase tracking-[0.25em] text-champagne"
                      >
                        <span className="border-b border-[#c9a227]/50 pb-1 group-hover/lm:border-[#c9a227] group-hover/lm:text-gold transition-colors">
                          Learn More
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover/lm:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          ))}

          {/* FINALE — the stage */}
          <motion.div
            className="min-h-[130vh] flex items-center justify-center px-6 lg:px-12"
            onViewportEnter={() => {
              if (!confettiFired.current) {
                confettiFired.current = true;
                fireConfetti();
              }
            }}
            onViewportLeave={() => {
              confettiFired.current = false;
            }}
          >
            <Reveal
              className="relative text-center max-w-3xl bg-[#fffdf9] border border-[#e3d3ac] p-10 lg:p-16 shadow-[0_30px_80px_rgba(45,26,11,0.25)]"
            >
              <span
                className="absolute bottom-0 left-0 right-0 h-[3px]"
                style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
              />
              <span
                className="mx-auto w-11 h-11 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
              >
                <span className="-rotate-45 font-serif italic text-white text-base">E</span>
              </span>
              <p className="flex items-center justify-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-stone mb-6">
                <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
                Center Stage
                <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
              </p>
              <h2 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream leading-[1.08]">
                Headliner <span className="italic gold-text">status.</span>
              </h2>
              <span className="block mt-8 mx-auto h-px w-24 bg-gradient-to-r from-transparent via-[#c9a227] to-transparent" />
              <p className="mt-8 text-base sm:text-lg text-sand leading-relaxed max-w-xl mx-auto">
                You've toured every gate. The stage is set, the lights are
                warm — all that's left is to unlock the vault and let our team
                build your offer.
              </p>
              <Link
                to="/apply"
                data-testid="cta-unlock-vault-finale"
                className="sheen-btn mt-10 inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-10 py-5 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform pointer-events-auto"
                style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
              >
                Take The Stage <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* TRINITY */}
      <section data-testid="trinity-section" className="relative overflow-hidden py-28 lg:py-36 px-6 lg:px-12 bg-espresso-deep border-y border-[rgba(212,175,55,0.12)]">
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-8 left-1/2 -translate-x-1/2 font-serif italic whitespace-nowrap text-[18vw] leading-none text-transparent"
          style={{ WebkitTextStroke: "1px rgba(201,162,39,0.10)" }}
        >
          Trinity
        </span>
        <div className="relative max-w-7xl mx-auto">
          <Reveal className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#d4af37]" />
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <p className="text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold">
                The Trinity of Approval
              </p>
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#d4af37]" />
            </div>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight text-cream max-w-3xl mx-auto leading-[1.12]">
              Three pillars anchor <span className="italic gold-text">every deal</span> we close for you.
            </h2>
            <p className="mt-6 text-base text-sand leading-relaxed max-w-xl mx-auto">
              From clarifying your goals to positioning you as irresistibly
              lendable.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {TRINITY.map((t, i) => (
              <Reveal key={t.num} delay={i * 0.14}>
                <div
                  data-testid={`trinity-pillar-${i + 1}`}
                  className="group relative bg-[#4D4232] border border-[#6a5c48] p-10 lg:p-12 h-full shadow-[0_25px_70px_rgba(45,26,11,0.3)] transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_40px_90px_rgba(201,162,39,0.35)]"
                >
                  <span
                    className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                  />
                  <span
                    aria-hidden="true"
                    className="absolute -right-2 -bottom-7 font-serif italic text-[8rem] leading-none text-transparent pointer-events-none select-none"
                    style={{ WebkitTextStroke: "1px rgba(212,175,55,0.18)" }}
                  >
                    {t.num}
                  </span>
                  <span
                    className="w-12 h-12 rotate-45 flex items-center justify-center shadow-[0_8px_20px_rgba(201,162,39,0.4)] transition-transform duration-700 group-hover:rotate-[135deg]"
                    style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                  >
                    <span className="-rotate-45 font-serif italic text-white text-lg transition-transform duration-700 group-hover:-rotate-[135deg]">
                      {t.num}
                    </span>
                  </span>
                  <h3 className="mt-9 font-serif text-2xl text-[#f3e5c8] leading-snug">{t.title}</h3>
                  <span className="mt-6 block h-px w-16 bg-gradient-to-r from-[#d4af37] to-transparent transition-all duration-500 group-hover:w-24" />
                  <p className="mt-6 text-sm text-[#cbb493] leading-relaxed">{t.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        data-testid="how-it-works-section"
        className="relative overflow-hidden py-28 lg:py-40 px-6 lg:px-12 border-y border-[rgba(212,175,55,0.25)]"
        style={{
          background:
            "radial-gradient(1200px 500px at 50% -10%, rgba(212,175,55,0.14), transparent 60%), linear-gradient(180deg, #4D4232 0%, #4D4232 55%, #4D4232 100%)",
        }}
      >
        {/* faint marquee-style backdrop word */}
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-6 left-1/2 -translate-x-1/2 font-serif italic whitespace-nowrap text-[16vw] leading-none text-transparent"
          style={{ WebkitTextStroke: "1px rgba(212,175,55,0.10)" }}
        >
          The Journey
        </span>

        <div className="relative max-w-7xl mx-auto">
          <Reveal className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#d4af37]" />
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <p className="text-xs font-mono-accent uppercase tracking-[0.35em] text-[#d4af37]">
                How It Works
              </p>
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#d4af37]" />
            </div>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f3e5c8] max-w-3xl mx-auto leading-[1.12]">
              From interested to <span className="italic gold-text">funded</span>,
              with clarity at every step.
            </h2>
            <p className="mt-6 text-base text-[#cbb493] leading-relaxed max-w-xl mx-auto">
              Four movements, one headline performance — your capital, on stage
              in days, not months.
            </p>
          </Reveal>

          {/* gold timeline track (desktop) */}
          <div className="relative mt-20">
            <span className="hidden lg:block absolute top-[52px] left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-[rgba(212,175,55,0.55)] to-transparent" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
              {STEPS.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.14}>
                  <div
                    data-testid={`step-card-${s.num}`}
                    className="group relative h-full text-center px-7 pt-0 pb-10"
                  >
                    {/* icon medallion */}
                    <div className="relative z-10 mx-auto w-[104px] h-[104px] flex items-center justify-center">
                      <span className="absolute inset-0 rotate-45 border border-[rgba(212,175,55,0.45)] bg-[#4D4232] transition-all duration-500 group-hover:rotate-[135deg] group-hover:border-[#d4af37] group-hover:shadow-[0_0_35px_rgba(212,175,55,0.35)]" />
                      <s.icon
                        className="relative w-8 h-8 text-[#d4af37] transition-transform duration-500 group-hover:scale-110"
                        strokeWidth={1.4}
                      />
                    </div>

                    <span className="block mt-8 font-serif italic text-5xl leading-none text-transparent" style={{ WebkitTextStroke: "1px rgba(212,175,55,0.55)" }}>
                      {s.num}
                    </span>
                    <span className="mt-5 inline-block font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#d4af37] border border-[rgba(212,175,55,0.4)] px-3 py-1.5">
                      {s.tag}
                    </span>
                    <h3 className="mt-5 font-serif text-2xl text-[#f3e5c8]">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-sm text-[#b39a76] leading-relaxed">
                      {s.copy}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.2} className="mt-16 text-center">
            <Link
              to="/apply"
              data-testid="cta-unlock-vault-steps"
              className="sheen-btn inline-flex items-center gap-3 bg-gold text-[#4D4232] text-xs font-semibold uppercase tracking-[0.2em] px-10 py-5 hover:bg-gold-bright hover:scale-[1.02] transition-all"
            >
              Begin Step One <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-espresso-deep border-y border-[rgba(212,175,55,0.12)]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-xs font-mono-accent uppercase tracking-[0.3em] text-gold mb-4">
                The Vault Calculator
              </p>
              <h2 className="font-serif font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream">
                What could your catalog unlock?
              </h2>
              <div className="mt-12 space-y-10">
                <div>
                  <div className="flex justify-between items-baseline mb-4">
                    <label className="text-sm text-sand">Monthly streams</label>
                    <span className="font-mono-accent text-sm text-champagne">
                      {streams.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="10000000"
                    step="10000"
                    value={streams}
                    onChange={(e) => setStreams(Number(e.target.value))}
                    data-testid="calculator-streams-slider"
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-4">
                    <label className="text-sm text-sand">Monthly royalty income</label>
                    <span className="font-mono-accent text-sm text-champagne">
                      ${royalty.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="500000"
                    step="1000"
                    value={royalty}
                    onChange={(e) => setRoyalty(Number(e.target.value))}
                    data-testid="calculator-royalty-slider"
                    className="w-full"
                  />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-espresso-card border border-[rgba(212,175,55,0.3)] p-10 gold-border-glow">
                <p className="text-xs font-mono-accent uppercase tracking-[0.3em] text-stone mb-6">
                  Estimated Funding Potential
                </p>
                <p
                  data-testid="calculator-estimate"
                  className="font-serif text-4xl sm:text-5xl gold-text font-semibold"
                >
                  {fmt(low)} – {fmt(Math.max(high, low))}
                </p>
                <p className="mt-6 text-xs text-stone leading-relaxed">
                  Indicative only. Your actual offer depends on catalog history,
                  credit profile, and deal structure.
                </p>
                <Link
                  to="/apply"
                  data-testid="calculator-cta"
                  className="mt-8 inline-flex items-center gap-3 bg-gold text-[#4D4232] text-xs font-semibold uppercase tracking-[0.2em] px-8 py-4 hover:bg-gold-bright transition-colors"
                >
                  Get My Real Number <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS + TESTIMONIALS */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 max-w-7xl mx-auto">
        <div data-testid="stats-band" className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div
                data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative p-8 lg:p-10 text-center border border-[#4D4232] shadow-[0_25px_60px_rgba(60,40,10,0.3)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_35px_80px_rgba(201,162,39,0.45)]"
                style={{
                  background:
                    "linear-gradient(155deg, #b98f2a 0%, #e5c158 40%, #d4af37 65%, #8c6d3f 100%)",
                }}
              >
                <div className="absolute inset-2 border border-[rgba(77,68,50,0.45)] pointer-events-none" />
                <span className="absolute -top-1.5 -left-1.5 w-3 h-3 rotate-45 bg-[#4D4232]" />
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rotate-45 bg-[#4D4232]" />
                <span className="absolute -bottom-1.5 -left-1.5 w-3 h-3 rotate-45 bg-[#4D4232]" />
                <span className="absolute -bottom-1.5 -right-1.5 w-3 h-3 rotate-45 bg-[#4D4232]" />
                <span className="mx-auto block w-1.5 h-1.5 rotate-45 bg-[#4D4232] opacity-80" />
                <p className="mt-5 font-serif font-semibold text-4xl sm:text-5xl text-[#4D4232] tracking-tight">
                  <StatNumber prefix={s.prefix} value={s.value} suffix={s.suffix} />
                </p>
                <span className="mx-auto mt-5 block h-px w-10 bg-[#4D4232]/60 transition-all duration-500 group-hover:w-16 group-hover:bg-[#4D4232]" />
                <p className="mt-5 text-[10px] sm:text-xs font-mono-accent uppercase tracking-[0.3em] text-[#4D4232]">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <figure className="h-full border-l-2 border-gold/60 pl-8 py-4">
                <blockquote className="font-serif italic text-lg sm:text-xl text-white leading-relaxed">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 text-xs font-mono-accent uppercase tracking-[0.2em] text-gold">
                  {t.role}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section data-testid="faq-preview-section" className="relative overflow-hidden py-28 lg:py-36 px-6 lg:px-12 bg-espresso-deep border-y border-[rgba(212,175,55,0.12)]">
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-8 left-1/2 -translate-x-1/2 font-serif italic whitespace-nowrap text-[18vw] leading-none text-transparent"
          style={{ WebkitTextStroke: "1px rgba(201,162,39,0.10)" }}
        >
          Answers
        </span>
        <div className="relative max-w-4xl mx-auto">
          <Reveal className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#d4af37]" />
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <p className="text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold">
                Before You Ask
              </p>
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#d4af37]" />
            </div>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight text-cream leading-[1.12]">
              The questions <span className="italic gold-text">every artist</span> asks first.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-5">
            {FAQ_PREVIEW.map((f, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div
                  data-testid={`faq-preview-card-${i + 1}`}
                  className="group relative bg-[#4D4232] border border-[#6a5c48] p-8 lg:p-10 shadow-[0_20px_55px_rgba(45,26,11,0.3)] transition-all duration-700 hover:-translate-y-1.5 hover:shadow-[0_30px_70px_rgba(201,162,39,0.3)]"
                >
                  <span
                    className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                  />
                  <div className="flex items-start gap-6">
                    <span
                      className="w-10 h-10 rotate-45 flex items-center justify-center shrink-0 shadow-[0_8px_20px_rgba(201,162,39,0.4)] transition-transform duration-700 group-hover:rotate-[135deg]"
                      style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                    >
                      <span className="-rotate-45 font-serif italic text-white text-base transition-transform duration-700 group-hover:-rotate-[135deg]">Q</span>
                    </span>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl text-[#f3e5c8] leading-snug">{f.q}</h3>
                      <span className="mt-4 block h-px w-14 bg-gradient-to-r from-[#d4af37] to-transparent transition-all duration-500 group-hover:w-20" />
                      <p className="mt-4 text-sm text-[#cbb493] leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 text-center">
            <Link
              to="/faq"
              data-testid="faq-preview-more"
              className="btn-outline-lux inline-flex items-center gap-3 text-champagne text-[11px] font-semibold uppercase tracking-[0.25em] px-8 py-4 hover:text-gold"
            >
              View All Questions <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* VAULT CTA */}
      <section
        data-testid="vault-cta-section"
        className="relative py-32 lg:py-40 px-6 lg:px-12 overflow-hidden border-y border-[#4D4232]"
        style={{
          background:
            "linear-gradient(155deg, #a87f24 0%, #e5c158 38%, #d4af37 62%, #8c6d3f 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center mix-blend-multiply"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1616714109948-c74fe5029a4d?crop=entropy&cs=srgb&fm=jpg&q=85)",
          }}
        />
        <span className="absolute top-6 left-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <span className="absolute top-6 right-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <span className="absolute bottom-6 left-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <span className="absolute bottom-6 right-6 w-3 h-3 rotate-45 bg-[#4D4232] opacity-80" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="h-px w-12 sm:w-20 bg-[#4D4232]/50" />
              <span className="w-2 h-2 rotate-45 bg-[#4D4232]" />
              <p className="text-[10px] font-mono-accent uppercase tracking-[0.35em] text-[#4D4232]">
                The Vault Awaits
              </p>
              <span className="w-2 h-2 rotate-45 bg-[#4D4232]" />
              <span className="h-px w-12 sm:w-20 bg-[#4D4232]/50" />
            </div>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#4D4232] leading-[1.1]">
              Unlock the vault —{" "}
              <span className="italic text-[#5c3d10]">begin your application.</span>
            </h2>
            <p className="mt-8 text-base text-[#4a3418] max-w-xl mx-auto leading-relaxed">
              Five minutes. Zero obligation. A real answer on your funding
              potential within 24 hours.
            </p>
            <Link
              to="/apply"
              data-testid="cta-unlock-vault-bottom"
              className="mt-10 inline-flex items-center gap-3 bg-[#4D4232] text-[#e5c158] text-xs font-semibold uppercase tracking-[0.2em] px-10 py-5 shadow-[0_15px_40px_rgba(45,26,11,0.4)] hover:bg-[#3d3427] hover:scale-[1.02] transition-all"
            >
              Begin Application <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-xs font-mono-accent uppercase tracking-[0.15em] text-[#4a3418]">
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-[#4D4232]" /> 100% rights retained
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-[#4D4232]" /> Offers in a week or less
              </span>
              <span className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#4D4232]" /> $25K – $10M+
              </span>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
